import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import SchemaOrg from '../components/SchemaOrg';

const ContactoPage: React.FC = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    tipo: 'Particular',
    empresa: '',
    email: '',
    telefono: '',
    consulta: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Formatear mensaje para WhatsApp
    const phone = "5491139416257";
    const businessText = formData.tipo === 'Empresa' ? `🏢 *Empresa:* ${formData.empresa}` : `👤 *Tipo:* Particular`;

    const message = `¡Hola LQP Global! 🚀\n\nAcabo de completar el formulario de contacto:\n\n👤 *Nombre:* ${formData.nombre}\n${businessText}\n📧 *Email:* ${formData.email}\n📞 *Teléfono:* ${formData.telefono}\n📝 *Consulta:* ${formData.consulta}`;

    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

    // Abrir WhatsApp
    window.open(whatsappUrl, '_blank');
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="pt-48 pb-24 min-h-screen flex items-center justify-center">
        <SEO
          title="Mensaje Enviado | Contacto - LQP Global"
          description="Tu mensaje ha sido enviado correctamente. Nos pondremos en contacto contigo a la brevedad."
          url="https://lqpglobal.com/contacto"
        />
        <div className="glass p-12 rounded-[3rem] border-white/10 text-center max-w-lg">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-8">
            <span className="text-4xl" aria-hidden="true">✅</span>
          </div>
          <h1 className="text-3xl font-black mb-4">¡WhatsApp Iniciado!</h1>
          <p className="text-gray-400 mb-8">
            Se ha abierto una pestaña con tu mensaje pre-cargado. Por favor, dale a "enviar" en WhatsApp para que podamos recibir tu consulta.
          </p>
          <Link
            to="/contacto"
            onClick={() => setSubmitted(false)}
            className="inline-block px-8 py-4 bg-white text-black font-bold rounded-xl transition-all"
          >
            Enviar otra consulta
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24">
      <SEO
        title="Contacto | Consultoría Gratuita - LQP Global"
        description="Contactá a LQP Global para una consultoría gratuita. Desarrollamos soluciones digitales a medida para pymes. Respuesta inmediata por WhatsApp."
        keywords="contacto lqp global, consultoría software, presupuesto desarrollo web, contactar desarrolladores, whatsapp lqp"
        url="https://lqpglobal.com/contacto"
      />
      <SchemaOrg type="localBusiness" />

      <div className="container mx-auto px-6">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-8">
          <ol className="flex items-center gap-2 text-sm text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" className="hover:text-white transition-colors" itemProp="item">
                <span itemProp="name">Inicio</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <span>/</span>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-gray-400" itemProp="name">Contacto</span>
              <meta itemProp="position" content="2" />
            </li>
          </ol>
        </nav>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="inline-block px-4 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-xs font-bold text-red-500 mb-6 uppercase tracking-widest">
              Contacto Directo
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-gradient mb-8 leading-tight">
              Iniciemos tu <br /> <span className="text-red-500">Transformación</span>
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed mb-12">
              Completá el formulario. Al finalizar, se abrirá un chat de WhatsApp con toda tu información para que la comunicación sea instantánea.
            </p>

            <address className="space-y-8 not-italic">
              <div className="flex items-center gap-6">
                <div className="w-12 h-12 glass rounded-2xl flex items-center justify-center text-xl" aria-hidden="true">📍</div>
                <div>
                  <h2 className="font-bold">Ubicación</h2>
                  <p className="text-gray-500 text-sm">Buenos Aires, Argentina (Disponibilidad Global)</p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="w-12 h-12 glass rounded-2xl flex items-center justify-center text-xl" aria-hidden="true">✉️</div>
                <div>
                  <h2 className="font-bold">E-mail</h2>
                  <p className="text-gray-500 text-sm">
                    <a href="mailto:proyectos@lqpglobal.com" className="hover:text-white transition-colors">
                      proyectos@lqpglobal.com
                    </a>
                  </p>
                </div>
              </div>
            </address>
          </div>

          <div className="glass p-8 md:p-12 rounded-[3rem] border-white/10 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 blur-[60px] rounded-full pointer-events-none" aria-hidden="true" />

            <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="nombre" className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                    Nombre Completo
                  </label>
                  <input
                    id="nombre"
                    required
                    type="text"
                    autoComplete="name"
                    className="w-full px-6 py-4 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-red-500 transition-colors"
                    placeholder="Juan Pérez"
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="tipo" className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                    Tipo de Cliente
                  </label>
                  <select
                    id="tipo"
                    className="w-full px-6 py-4 rounded-xl bg-[#0a0a0a] border border-white/10 text-white focus:outline-none focus:border-red-500 transition-colors appearance-none"
                    onChange={(e) => setFormData({...formData, tipo: e.target.value})}
                  >
                    <option value="Particular">Particular</option>
                    <option value="Empresa">Empresa</option>
                  </select>
                </div>
              </div>

              {formData.tipo === 'Empresa' && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                  <label htmlFor="empresa" className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                    Nombre de la Empresa
                  </label>
                  <input
                    id="empresa"
                    required
                    type="text"
                    autoComplete="organization"
                    className="w-full px-6 py-4 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-red-500 transition-colors"
                    placeholder="Mi Empresa S.A."
                    onChange={(e) => setFormData({...formData, empresa: e.target.value})}
                  />
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                    E-mail Corporativo
                  </label>
                  <input
                    id="email"
                    required
                    type="email"
                    autoComplete="email"
                    className="w-full px-6 py-4 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-red-500 transition-colors"
                    placeholder="nombre@empresa.com"
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
                <div>
                  <label htmlFor="telefono" className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                    Teléfono / WhatsApp
                  </label>
                  <input
                    id="telefono"
                    required
                    type="tel"
                    autoComplete="tel"
                    className="w-full px-6 py-4 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-red-500 transition-colors"
                    placeholder="+54 11 ..."
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="consulta" className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                  Tu Consulta
                </label>
                <textarea
                  id="consulta"
                  required
                  rows={4}
                  className="w-full px-6 py-4 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-red-500 transition-colors resize-none"
                  placeholder="Contanos sobre tu negocio y qué procesos necesitás mejorar..."
                  onChange={(e) => setFormData({...formData, consulta: e.target.value})}
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full py-5 bg-red-600 hover:bg-red-700 text-white font-black rounded-2xl transition-all shadow-xl shadow-red-600/20 transform hover:-translate-y-1 flex items-center justify-center gap-3"
              >
                <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.316 1.592 5.43 0 9.856-4.426 9.858-9.855.002-5.43-4.425-9.856-9.856-9.856-5.43 0-9.856 4.426-9.858 9.856-.001 1.938.529 3.754 1.534 5.344l-1.01 3.687 3.782-.992zm11.722-6.873c-.32-.16-1.89-.932-2.21-1.048-.32-.115-.553-.173-.785.173-.23.348-.892 1.102-1.096 1.334-.202.231-.406.26-.726.1-.319-.16-1.348-.497-2.569-1.585-.949-.845-1.59-1.888-1.776-2.199-.187-.311-.02-.479.14-.638.144-.143.32-.375.48-.563.16-.188.214-.32.32-.535.106-.215.053-.404-.026-.563-.08-.16-.785-1.892-1.076-2.589-.282-.676-.57-.585-.785-.595-.203-.01-.436-.011-.669-.011-.232 0-.61.087-.93.433-.32.348-1.221 1.196-1.221 2.915 0 1.719 1.25 3.379 1.425 3.61.174.231 2.458 3.754 5.957 5.266.832.359 1.481.574 1.985.734.835.266 1.596.228 2.197.138.67-.1 1.89-.773 2.158-1.479.267-.706.267-1.313.187-1.442-.08-.129-.294-.207-.614-.367z"/>
                </svg>
                ENVIAR POR WHATSAPP
              </button>

              <p className="text-[10px] text-gray-600 text-center uppercase tracking-widest">
                Protección de datos garantizada. Serás redirigido a WhatsApp.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactoPage;
